import type {
  GroupBuyFulfillmentRecord,
  ReconcileGroupBuyFulfillmentRequest,
} from "@billiard/contracts";

export function normalizeGroupBuyPlatformReference(value: string): string {
  return value.replace(/\s+/gu, "");
}

function isCanonicalBusinessDate(value: string): boolean {
  const milliseconds = Date.parse(`${value}T00:00:00.000Z`);
  return (
    Number.isFinite(milliseconds) && new Date(milliseconds).toISOString().slice(0, 10) === value
  );
}

export function isGroupBuyFulfillmentSemanticallyConsistent(
  record: GroupBuyFulfillmentRecord,
): boolean {
  const startedAtMilliseconds = Date.parse(record.coverageStartedAt);
  const endsAtMilliseconds = Date.parse(record.coverageEndsAt);
  const fulfilledAtMilliseconds = Date.parse(record.fulfilledAt);
  const reconciledAtMilliseconds =
    record.reconciledAt === null ? null : Date.parse(record.reconciledAt);
  const actualAmounts = [record.actualSettlementFen, record.actualFeeFen, record.actualSubsidyFen];
  const money = [
    record.voucherCoveredFen,
    record.customerSupplementFen,
    record.expectedSettlementFen,
    record.expectedFeeFen,
    record.expectedSubsidyFen,
    ...actualAmounts,
  ];
  if (
    !isCanonicalBusinessDate(record.businessDate) ||
    normalizeGroupBuyPlatformReference(record.platformReference) !== record.platformReference ||
    money.some(
      (amount) =>
        amount !== null && (!Number.isSafeInteger(amount) || amount < 0 || amount > 100_000_000),
    ) ||
    !Number.isFinite(startedAtMilliseconds) ||
    !Number.isFinite(endsAtMilliseconds) ||
    !Number.isFinite(fulfilledAtMilliseconds) ||
    endsAtMilliseconds - startedAtMilliseconds !==
      record.packageSnapshot.includedMinutes * 60_000 ||
    fulfilledAtMilliseconds < startedAtMilliseconds
  ) {
    return false;
  }
  if (record.reconciliationOutcome === "PENDING") {
    return (
      actualAmounts.every((amount) => amount === null) &&
      record.reconciledAt === null &&
      record.reconciliationNote === null &&
      record.authorizerId === null
    );
  }
  return (
    actualAmounts.every((amount) => amount !== null) &&
    reconciledAtMilliseconds !== null &&
    Number.isFinite(reconciledAtMilliseconds) &&
    reconciledAtMilliseconds >= fulfilledAtMilliseconds &&
    record.reconciliationNote !== null &&
    record.reconciliationNote.trim().length >= 2 &&
    record.authorizerId !== null &&
    isGroupBuyReconciliationConsistent(record, record)
  );
}

export function isGroupBuyReconciliationConsistent(
  fulfillment: Pick<
    GroupBuyFulfillmentRecord,
    "expectedSettlementFen" | "expectedFeeFen" | "expectedSubsidyFen"
  >,
  result:
    | Pick<
        ReconcileGroupBuyFulfillmentRequest,
        "outcome" | "actualSettlementFen" | "actualFeeFen" | "actualSubsidyFen"
      >
    | Pick<
        GroupBuyFulfillmentRecord,
        "reconciliationOutcome" | "actualSettlementFen" | "actualFeeFen" | "actualSubsidyFen"
      >,
): boolean {
  const outcome = "outcome" in result ? result.outcome : result.reconciliationOutcome;
  const amountsMatch =
    result.actualSettlementFen === fulfillment.expectedSettlementFen &&
    result.actualFeeFen === fulfillment.expectedFeeFen &&
    result.actualSubsidyFen === fulfillment.expectedSubsidyFen;

  switch (outcome) {
    case "CONFIRMED":
      return amountsMatch;
    case "NOT_SETTLED":
      return (
        result.actualSettlementFen === 0 &&
        result.actualFeeFen === 0 &&
        result.actualSubsidyFen === 0
      );
    case "AMOUNT_MISMATCH":
      return !amountsMatch;
    case "DUPLICATE_RECORD":
      return true;
    case "PENDING":
      return false;
  }
}
